/**
 * OrganizationGates — PublicFund / NonProfit / Church / Association /
 * Government / SchoolDistrict / IOLTA.
 *
 * Ported from SPFx ProceduralGates:
 *   - _validateRequiredFields (PublicFunds / Donation / ICS_Personal branch)
 *   - _validateAuthorizationGates (FINANCE_APPROVAL_TYPES — Finance approval)
 *   - _validateDocumentCollection (PUBFUNDS_FINANCE_AUTH)
 */

import type { CartSnapshot, GateResult } from '../workflow-engine/types';
import {
  buildCipGate,
  buildOfacGate,
  buildRequiredFieldGates,
  getByRole,
  getPrimary,
  type FieldSpec,
} from '../ProceduralGates';

const ORG_FIELDS: ReadonlyArray<FieldSpec> = [
  { field: 'name2', label: 'Entity name' },
  { field: 'taxId', label: 'EIN' },
  { field: 'addr', label: 'Street address' },
  { field: 'city', label: 'City' },
  { field: 'state', label: 'State' },
  { field: 'zip', label: 'ZIP code' },
];

const FINANCE_APPROVAL_TYPES: ReadonlyArray<string> = [
  'PublicFund',
  'Government',
  'SchoolDistrict',
];

export function runOrganizationGates(cart: CartSnapshot): GateResult[] {
  const gates: GateResult[] = [];
  const primary = getPrimary(cart);
  if (!primary) return gates;

  gates.push(...buildRequiredFieldGates(cart, primary, ORG_FIELDS));

  // Authorized signer / agent
  const signers = getByRole(cart, 'signer');
  if (signers.length === 0) {
    gates.push({
      gateCode: 'BIZ_AUTH_SIGNER',
      entityType: cart.entityType ?? '',
      category: 'relationship',
      status: 'fail',
      label: 'Authorized signer required',
      reason: 'Organization accounts require at least 1 authorized signer.',
      remediationAction: { label: 'Add signer', deepLinkStepId: 'people' },
    });
  }

  // Finance approval for public funds + government (SPFx AUTH_FINANCE_REQUIRED)
  if (FINANCE_APPROVAL_TYPES.includes(cart.entityType as string)) {
    const financeApproved = cart.metadata?.financeApproved === true;
    gates.push({
      gateCode: 'AUTH_FINANCE_REQUIRED',
      entityType: cart.entityType ?? '',
      category: 'authorization',
      status: financeApproved ? 'pass' : 'fail',
      label: 'Finance department approval required',
      reason: financeApproved
        ? undefined
        : `${cart.entityType} accounts require Finance department approval before proceeding.`,
      remediationAction: financeApproved
        ? undefined
        : { label: 'Request Finance approval', deepLinkStepId: 'compliance' },
    });

    gates.push({
      gateCode: 'PUBFUNDS_FINANCE_AUTH',
      entityType: cart.entityType ?? '',
      category: 'documentCollection',
      status: cart.documents.some((d) => d.docType === 'FINANCE_APPROVE' && d.collected) ? 'pass' : 'fail',
      label: 'Finance authorization document collected',
      reason: 'Public Funds accounts require Finance department authorization to be collected.',
      remediationAction: { label: 'Upload authorization', deepLinkStepId: 'compliance' },
    });
  }

  // Non-profit — IRS determination letter (501(c))
  if (cart.entityType === 'NonProfit' || cart.entityType === 'Church') {
    gates.push({
      gateCode: 'DOC_MISSING_IRS_DETERMINATION',
      entityType: cart.entityType ?? '',
      category: 'documentCollection',
      status: cart.documents.some((d) => d.docType === 'IRS_DETERMINATION' && d.collected) ? 'pass' : 'warn',
      label: 'IRS determination letter (501(c))',
      reason: '501(c) determination letter recommended for tax-exempt status.',
      remediationAction: { label: 'Upload IRS letter', deepLinkStepId: 'compliance' },
    });
  }

  // CDD auto-exempt for government entities (SPFx CddCipSection AUTO_EXEMPT_GOVT)
  if (
    cart.entityType === 'PublicFund' ||
    cart.entityType === 'Government' ||
    cart.entityType === 'SchoolDistrict'
  ) {
    gates.push({
      gateCode: 'CDD',
      entityType: cart.entityType ?? '',
      category: 'compliance',
      status: 'pass',
      label: 'Customer Due Diligence (CDD)',
      reason: 'Auto-exempt — Government (Core Director field 378 = 15).',
    });
  }

  gates.push(buildCipGate(cart));
  gates.push(buildOfacGate(cart));

  return gates;
}
